export class Configuration {
  dimensions: Dimensions
  homeImg: string
  initialOverlay: Overlay
  videos: Video[]
  sections: Section[]

  constructor(
    dimensions: Dimensions,
    homeImg: string,
    initialOverlay: Overlay,
    videos: Video[],
    sections: Section[]
  ) {
    this.dimensions = dimensions
    this.homeImg = homeImg
    this.initialOverlay = initialOverlay
    this.videos = videos
    this.sections = sections
  }

  get homeSection(): Section {
    return this.sections[0]
  }
}

export class Section {
  id: string
  name: string
  icon: string
  visibleSections: string[]
  autoplay: boolean

  constructor(
    id: string,
    name: string,
    icon: string,
    visibleSections: string[] = [],
    autoplay: boolean = false
  ) {
    this.id = id
    this.name = name
    this.icon = icon
    this.visibleSections = visibleSections
    this.autoplay = autoplay
  }
}

export class OnEndActions {
  goToSection: string | null
  openOverlay: boolean

  constructor(goToSection: string | null, openOverlay: boolean) {
    this.goToSection = goToSection
    this.openOverlay = openOverlay
  }


  public static goToSection(section: string): OnEndActions {
    return new OnEndActions(section, false)
  }

  public static openOverlay(): OnEndActions {
    return new OnEndActions(null, true)
  }
}

export class Video {
  id: string
  url: string
  overlay: Overlay
  section: string
  onEndActions: OnEndActions

  constructor(
    id: string,
    url: string,
    overlay: Overlay,
    section: string,
    onEndActions: OnEndActions
  ) {
    this.id = id
    this.url = url
    this.overlay = overlay
    this.section = section
    this.onEndActions = onEndActions
  }
}

export class Overlay {
  items: any[] = []
  image?: string

  constructor(items: TOverlayItem[] = [], image?: string) {
    this.items = items
    this.image = image
  }
}

export type TOverlayItem = OverlayText | OverlayImage | OverlayMix

export interface IOverlayItem {
  id: string
  posX: number
  posY: number
  align: TAlignment
  /**
   * Percentage (0 to 1) of how big the item is in proportion to screen's
   * width (video player). Considering that 1 is an item that occupies the
   * whole screen.
   */
  proportion: number
  videoId?: string | null
  section?: string | null
  link?: string | null
}

export abstract class OverlayItem implements IOverlayItem {
  id: string
  posX: number
  posY: number
  align: TAlignment
  /**
   * Percentage (0 to 1) of how big the item is in proportion to screen's
   * width (video player). Considering that 1 is an item that occupies the
   * whole screen.
   */
  proportion: number
  videoId: string | null = null
  section: string | null = null
  link: string | null = null

  protected constructor(obj: IOverlayItem) {
    this.id = obj.id
    this.posX = obj.posX
    this.posY = obj.posY
    this.align = obj.align
    this.proportion = obj.proportion
    this.videoId = obj.videoId ?? null
    this.section = obj.section ?? null
    this.link = obj.link ?? null
  }
}

export interface IOverlayImage extends IOverlayItem {
  img: string
}

export class OverlayImage extends OverlayItem implements IOverlayImage {
  img: string
  dimensions?: Dimensions

  constructor(obj: IOverlayImage) {
    super(obj)
    this.img = obj.img
  }
}

export interface IOverlayText extends IOverlayItem {
  text: string
  type: TTitle
}

export class OverlayText extends OverlayItem implements IOverlayText {
  text: string
  type: TTitle
  fontSize: number = 0

  constructor(obj: IOverlayText) {
    super(obj)
    this.text = obj.text
    this.type = obj.type
    this.align = obj.align
  }
}

export interface IOverlayMixImage {
  img: string
  proportion: number
}

export interface IOverlayMixText {
  text: string
  type: TTitle
  proportion: number
}

export class OverlayMix extends OverlayItem {
  image: OverlayImage
  title: OverlayText

  constructor(obj: Omit<IOverlayItem, 'proportion'>, image: IOverlayMixImage, title: IOverlayMixText) {
    super({...obj, proportion: 0})
    this.image = new OverlayImage({...obj, ...image})
    this.title = new OverlayText({...obj, ...title})
  }
}

export type TTitle = 'title' | 'option' | 'text'
export type TAlignment = 'left' | 'center' | 'right' | 'top' | 'bottom'

export class Dimensions {
  ratio: number
  width: number
  height: number

  constructor(width: number, height: number) {
    this.width = width
    this.height = height
    this.ratio = width / height
  }
}